import { TSR_SET_SPORT_FEEDS, type TsrLiveMatchState, type TsrMatchStats, type TsrSetSportSlug, type TsrStatsEvent, type TsrTeam } from "./socket";

/**
 * TSR's live set-sport match as OVR reads it over REST — the public
 * `/tsr/<slug>/:id/state` and (sepak takraw) `/tsr/<slug>/:id/stats` — turned
 * back into the shapes the Save Meet mapper (`tsrSetsSaveBody`) takes. Pure.
 *
 * The REST envelope snake-cases every key (`score_a`, `completed_sets`) and
 * lower-cases all-caps ones; the socket's state is camelCase. So the state's
 * keys are camel-cased back here, and the stats log is rebuilt from the
 * projected events' VALUES (`team`, `action` — values keep their case), never
 * from the per-side totals, whose `A` / `B` keys came back as `a` / `b`.
 */

/** The `/tsr/<slug>` feed a meet's sport is served on, by code (`TSR_SET_SPORT_FEEDS`); null = no live set-sport feed. */
export function tsrSetFeedSlugFor(sportCode: string | null | undefined): TsrSetSportSlug | null {
	const code = (sportCode ?? "").trim().toUpperCase();
	for (const [slug, feed] of Object.entries(TSR_SET_SPORT_FEEDS) as [TsrSetSportSlug, { sportCodes: readonly string[] }][]) {
		if (feed.sportCodes.includes(code)) return slug;
	}
	return null;
}

/** The feeds with a Stats Controller (`/stats` answers 404 for the others). */
export const TSR_STATS_FEEDS: readonly TsrSetSportSlug[] = ["sepak-takraw"];

const camel = (k: string) => k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());

/** Keys snake → camel, deep. Values untouched. Every TSR live read over REST (sets here, boxing in `boxing.ts`). */
export function tsrCamelKeys(v: unknown): unknown {
	if (Array.isArray(v)) return v.map(tsrCamelKeys);
	if (v && typeof v === "object") return Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [camel(k), tsrCamelKeys(x)]));
	return v;
}

const isInt = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v) && v >= 0;
const isSide = (v: unknown): v is TsrTeam => v === "A" || v === "B";

/**
 * `GET /tsr/<slug>/:id/state` → `data.state` as the controller's state, or
 * null when it is not one (no scores, no sets list). Completed sets keep only
 * well-formed `{ a, b }` rows.
 */
export function tsrLiveStateFromWire(wire: unknown): TsrLiveMatchState | null {
	if (!wire || typeof wire !== "object" || Array.isArray(wire)) return null;
	const s = tsrCamelKeys(wire) as Record<string, unknown>;
	if (!isInt(s.scoreA) || !isInt(s.scoreB) || !Array.isArray(s.completedSets)) return null;
	const completedSets = (s.completedSets as Record<string, unknown>[]).filter((c) => c && isInt(c.a) && isInt(c.b)).map((c) => ({ ...c, winner: isSide(c.winner) ? c.winner : null }));
	return {
		...s,
		maxSets: isInt(s.maxSets) && s.maxSets > 0 ? s.maxSets : 3,
		maxGames: isInt(s.maxGames) && s.maxGames > 0 ? s.maxGames : 1,
		multiGame: s.multiGame === true,
		currentSet: isInt(s.currentSet) ? s.currentSet : completedSets.length + 1,
		completedSets: completedSets as TsrLiveMatchState["completedSets"],
		scoreA: s.scoreA,
		scoreB: s.scoreB,
		setsWonA: isInt(s.setsWonA) ? s.setsWonA : 0,
		setsWonB: isInt(s.setsWonB) ? s.setsWonB : 0,
		currentGame: isInt(s.currentGame) && s.currentGame > 0 ? s.currentGame : 1,
		gameWinsA: isInt(s.gameWinsA) ? s.gameWinsA : 0,
		gameWinsB: isInt(s.gameWinsB) ? s.gameWinsB : 0,
		gameHistory: Array.isArray(s.gameHistory) ? (s.gameHistory as TsrLiveMatchState["gameHistory"]) : [],
		servingTeam: isSide(s.servingTeam) ? s.servingTeam : "A",
		matchFinished: s.matchFinished === true,
	};
}

// ─── The statistics board (`GET /tsr/sepak-takraw/:id/stats`) ───────────────

/** One side's totals, as the feed counts them (points credited, winners, points from the opponent's errors, errors committed, longest run in one set, points per action key). */
export interface TsrStatsSideTotals {
	points: number;
	winners: number;
	fromErrors: number;
	errorsCommitted: number;
	longestRun: number;
	byAction: Record<string, number>;
}

/** One athlete's line, top scorer first. A point logged to the team is in no line. */
export interface TsrStatsPlayerLine {
	name: string;
	points: number;
	errors: number;
	byAction: Record<string, number>;
}

/** Where a side's points ended: `cells[band][lane]` (bands Net, Mid, Back; lanes Left, Centre, Right), `out` landed out. */
export interface TsrStatsLanding {
	total: number;
	out: number;
	cells: number[][];
}

export interface TsrStatsPoint {
	no: number;
	id: string;
	team: TsrTeam;
	action: string;
	actionLabel: string;
	kind: "winner" | "error";
	detail: string | null;
	player: string | null;
	playerTeam: TsrTeam | null;
	set: number;
	game: number;
	score: { a: number; b: number } | null;
	zone: string | null;
	at: string | null;
}

/**
 * The whole statistics response, as the venue's statistics board reads it
 * (the "Sepak Takraw Stats Feed" contract): sides by meet participant, totals
 * and players per side, landing zones, the last point, the action list (labels
 * come from here, never a hard-coded table), and the log `tsrSetsSaveBody`
 * counts. Built fresh from every response — an undone point simply is not
 * there next time.
 */
export interface TsrStatsBoard {
	rev: number;
	updatedAt: string | null;
	pointsLogged: number;
	scope: { kind: "match" | "set"; set: number; game: number };
	/** The meet participant (slot) id of side A / B, when the feed resolved the match to two sides. */
	sides: Record<TsrTeam, string | null> | null;
	totals: Record<TsrTeam, TsrStatsSideTotals>;
	players: Record<TsrTeam, TsrStatsPlayerLine[]>;
	landing: { depthBands: string[]; lanes: string[]; A: TsrStatsLanding; B: TsrStatsLanding };
	lastPoint: TsrStatsPoint | null;
	actions: { key: string; label: string; short: string; kind: "winner" | "error" }[];
	/** The point log, for `tsrSetsSaveBody`'s team statistics. */
	stats: TsrMatchStats;
}

const nOr0 = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const sOrNull = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);
const rec = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
/** A per-side map: the contract's `A` / `B`, or the envelope's lower-cased `a` / `b`. */
const side = (v: unknown, s: TsrTeam): unknown => rec(v)[s] ?? rec(v)[s.toLowerCase()];
/** A key the contract spells in camelCase, or the envelope's snake_case. */
const pick = (o: Record<string, unknown>, camelKey: string): unknown => o[camelKey] ?? o[camelKey.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)];

const counts = (v: unknown): Record<string, number> =>
	Object.fromEntries(Object.entries(rec(v)).filter(([, n]) => typeof n === "number" && Number.isFinite(n)) as [string, number][]);

function totalsOf(v: unknown): TsrStatsSideTotals {
	const o = rec(v);
	return {
		points: nOr0(o.points),
		winners: nOr0(o.winners),
		fromErrors: nOr0(pick(o, "fromErrors")),
		errorsCommitted: nOr0(pick(o, "errorsCommitted")),
		longestRun: nOr0(pick(o, "longestRun")),
		byAction: counts(pick(o, "byAction")),
	};
}

function playersOf(v: unknown): TsrStatsPlayerLine[] {
	if (!Array.isArray(v)) return [];
	return v.flatMap((p) => {
		const o = rec(p);
		const name = sOrNull(o.name);
		return name ? [{ name, points: nOr0(o.points), errors: nOr0(o.errors), byAction: counts(pick(o, "byAction")) }] : [];
	});
}

function landingOf(v: unknown): TsrStatsLanding {
	const o = rec(v);
	const cells = Array.isArray(o.cells) ? (o.cells as unknown[]).map((row) => (Array.isArray(row) ? row.map(nOr0) : [])) : [];
	return { total: nOr0(o.total), out: nOr0(o.out), cells };
}

function pointOf(v: unknown): TsrStatsPoint | null {
	const o = rec(v);
	if (!isSide(o.team) || typeof o.action !== "string") return null;
	const score = rec(o.score);
	const court = rec(o.court);
	const pt = pick(o, "playerTeam");
	return {
		no: nOr0(o.no),
		id: sOrNull(o.id) ?? "",
		team: o.team,
		action: o.action,
		actionLabel: sOrNull(pick(o, "actionLabel")) ?? o.action,
		kind: o.kind === "error" ? "error" : "winner",
		detail: sOrNull(o.detail),
		player: sOrNull(o.player),
		playerTeam: isSide(pt) ? pt : null,
		set: nOr0(o.set) || 1,
		game: nOr0(o.game) || 1,
		score: isInt(score.a) && isInt(score.b) ? { a: score.a, b: score.b } : null,
		zone: sOrNull(court.zone),
		at: sOrNull(o.at),
	};
}

/** `GET /tsr/sepak-takraw/:id/stats` → `data`, read whole and defensively. */
export function tsrStatsBoardFromWire(data: unknown): TsrStatsBoard {
	const d = rec(data);
	const scope = rec(d.scope);
	const sides = rec(d.sides);
	const sideId = (s: TsrTeam) => sOrNull(pick(rec(side(sides, s)), "meetParticipantId"));
	const landing = rec(d.landing);
	const actions = Array.isArray(d.actions)
		? (d.actions as unknown[]).flatMap((a) => {
				const o = rec(a);
				return typeof o.key === "string" && o.key !== ""
					? [{ key: o.key, label: sOrNull(o.label) ?? o.key, short: sOrNull(o.short) ?? o.key.slice(0, 3).toUpperCase(), kind: o.kind === "error" ? ("error" as const) : ("winner" as const) }]
					: [];
			})
		: [];
	const hasSides = d.sides !== null && d.sides !== undefined && (sideId("A") !== null || sideId("B") !== null);
	return {
		rev: nOr0(d.rev),
		updatedAt: sOrNull(pick(d, "updatedAt")),
		pointsLogged: nOr0(pick(d, "pointsLogged")),
		scope: { kind: scope.kind === "set" ? "set" : "match", set: nOr0(scope.set) || 1, game: nOr0(scope.game) || 1 },
		sides: hasSides ? { A: sideId("A"), B: sideId("B") } : null,
		totals: { A: totalsOf(side(d.totals, "A")), B: totalsOf(side(d.totals, "B")) },
		players: { A: playersOf(side(d.players, "A")), B: playersOf(side(d.players, "B")) },
		landing: {
			depthBands: Array.isArray(pick(landing, "depthBands")) ? (pick(landing, "depthBands") as unknown[]).map(String) : [],
			lanes: Array.isArray(landing.lanes) ? (landing.lanes as unknown[]).map(String) : [],
			A: landingOf(side(landing, "A")),
			B: landingOf(side(landing, "B")),
		},
		lastPoint: pointOf(pick(d, "lastPoint")),
		actions,
		stats: tsrStatsFromWire(d.events),
	};
}

/**
 * `GET /tsr/sepak-takraw/:id/stats` → `data.events` as the Stats Controller
 * log `tsrSetsSaveBody` counts (`tsrSepakTakrawTeamStats`): each point's
 * credited side and action, its set and game. Malformed points are dropped.
 */
export function tsrStatsFromWire(events: unknown): TsrMatchStats {
	if (!Array.isArray(events)) return { events: [] };
	const out: TsrStatsEvent[] = [];
	for (const e of events as Record<string, unknown>[]) {
		if (!e || !isSide(e.team) || typeof e.action !== "string" || e.action === "") continue;
		const ev: TsrStatsEvent = { team: e.team, action: e.action };
		if (isInt(e.set)) ev.set = e.set;
		if (isInt(e.game)) ev.game = e.game;
		if (typeof e.player === "string") ev.player = e.player;
		const pt = e.player_team ?? e.playerTeam;
		if (isSide(pt)) ev.playerTeam = pt;
		out.push(ev);
	}
	return { events: out };
}
